import React, { useEffect, useState } from 'react';
import { Package } from 'lucide-react';
import {
  packagesService,
  type LessonPackageDto,
  type UpsertLessonPackageRequest,
} from '@/services/packages';
import { useToast } from '@/components/ui/use-toast';
import {
  PACKAGE_CURRENCIES,
  type PackageCurrency,
  centsToMajor,
  formatMoney,
  majorToCents,
} from '@/utils/money';
import {
  PromoCard,
  PromoCardBody,
  PromoCardDesc,
  PromoCardHeader,
  PromoCardTitle,
} from '@/components/promo/PromoCard';

type PackageFormState = {
  name: string;
  description: string;
  price: number;
  currencyCode: PackageCurrency;
  lessonsPerPeriod?: number;
  lessonDurationMinutes: number;
  billingType: 'one_time' | 'subscription';
  isActive: boolean;
  applyPriceChangeToExisting: boolean;
  sortOrder: number;
};

const emptyForm = (): PackageFormState => ({
  name: '',
  description: '',
  price: 99,
  currencyCode: 'EUR',
  lessonsPerPeriod: 4,
  lessonDurationMinutes: 45,
  billingType: 'subscription',
  isActive: false,
  applyPriceChangeToExisting: false,
  sortOrder: 0,
});

const toApiRequest = (form: PackageFormState): UpsertLessonPackageRequest => ({
  name: form.name,
  description: form.description,
  priceCents: majorToCents(form.price),
  currencyCode: form.currencyCode,
  lessonsPerPeriod: form.lessonsPerPeriod,
  lessonDurationMinutes: form.lessonDurationMinutes,
  billingType: form.billingType,
  isActive: form.isActive,
  applyPriceChangeToExisting: form.applyPriceChangeToExisting,
  sortOrder: form.sortOrder,
});

const fromPackage = (p: LessonPackageDto): PackageFormState => ({
  name: p.name,
  description: p.description ?? '',
  price: centsToMajor(p.priceCents),
  currencyCode: (PACKAGE_CURRENCIES.includes(p.currencyCode as PackageCurrency)
    ? p.currencyCode
    : 'EUR') as PackageCurrency,
  lessonsPerPeriod: p.lessonsPerPeriod,
  lessonDurationMinutes: p.lessonDurationMinutes,
  billingType: p.billingType,
  isActive: p.isActive,
  applyPriceChangeToExisting: p.applyPriceChangeToExisting,
  sortOrder: p.sortOrder,
});

const TeacherPackagesPage: React.FC = () => {
  const { toast } = useToast();
  const [packages, setPackages] = useState<LessonPackageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<LessonPackageDto | null>(null);
  const [form, setForm] = useState<PackageFormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setPackages(await packagesService.list());
    } catch {
      toast({ title: 'Error', description: 'Failed to load packages', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm());
  };

  const startEdit = (p: LessonPackageDto) => {
    setEditing(p);
    setForm(fromPackage(p));
  };

  const save = async () => {
    if (!form.name.trim()) return;
    if (form.price <= 0) {
      toast({ title: 'Invalid price', description: 'Enter a price greater than zero.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = toApiRequest(form);
      let saved: LessonPackageDto;
      if (editing) {
        saved = await packagesService.update(editing.id, payload);
        toast({ title: 'Updated', description: 'Package saved.' });
      } else {
        saved = await packagesService.create(payload);
        toast({ title: 'Created', description: 'Package added to your catalog.' });
      }
      if (saved.billingWarning) {
        toast({
          title: 'Billing not fully linked',
          description: saved.billingWarning,
          variant: 'destructive',
        });
      }
      setEditing(null);
      setForm(emptyForm());
      await load();
    } catch {
      toast({ title: 'Error', description: 'Failed to save package', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p: LessonPackageDto) => {
    const payload = toApiRequest(fromPackage(p));
    await packagesService.update(p.id, { ...payload, isActive: !p.isActive, applyPriceChangeToExisting: false });
    await load();
  };

  const preview = packages.find((p) => p.isActive) || packages[0];
  const priceSuffix = form.billingType === 'subscription' ? ' / period' : ' one-time';

  return (
    <div className="space-y-4">
      <PromoCard>
        <PromoCardHeader>
          <PromoCardTitle icon={<Package size={20} strokeWidth={2} />}>Lesson packages</PromoCardTitle>
          <PromoCardDesc>
            Set prices in your currency (EUR, USD, GBP). When you mark a subscription package active,
            checkout is created automatically — you do not enter Lemon store or variant IDs.
          </PromoCardDesc>
        </PromoCardHeader>
        <PromoCardBody>
          <div className="pkg-grid">
            <div className="pkg-pane">
              <div className="pkg-pane-h">
                Your catalog
                <button type="button" className="pill-btn primary" onClick={startCreate}>
                  New package
                </button>
              </div>
              <div className="pkg-pane-b">
                {loading ? (
                  <p className="promo-empty">Loading…</p>
                ) : packages.length === 0 ? (
                  <p className="promo-empty">No packages yet.</p>
                ) : (
                  packages.map((p) => (
                    <div key={p.id} className="pkg-row">
                      <div>
                        <div className="pn">{p.name}</div>
                        <div className="pd">
                          {formatMoney(p.priceCents, p.currencyCode)}
                          {p.billingType === 'subscription' ? ' / period' : ' one-time'} ·{' '}
                          {p.lessonsPerPeriod ?? '—'} lessons · {p.lessonDurationMinutes} min
                          {p.activeSubscriberCount > 0
                            ? ` · ${p.activeSubscriberCount} subs`
                            : ''}
                          {p.isActive && p.billingType === 'subscription' && p.billingLinked
                            ? ' · billing linked'
                            : p.isActive && p.billingType === 'subscription'
                              ? ' · billing pending'
                              : ''}
                        </div>
                      </div>
                      <div className="flex gap-1 items-center">
                        <button type="button" className="pill-btn" onClick={() => startEdit(p)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className={`pill-btn ${p.isActive ? '' : 'primary'}`}
                          onClick={() => void toggleActive(p)}
                        >
                          {p.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="pkg-pane pkg-std">
              <div className="pkg-pane-h">{editing ? 'Edit package' : 'New package'}</div>
              <div className="pkg-pane-b pkg-form">
                <input
                  className="studio-field text-sm font-normal"
                  placeholder="Package name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <textarea
                  className="studio-field text-sm font-normal min-h-[60px]"
                  placeholder="Description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
                <div className="pkg-price-row">
                  <label className="pkg-form-label flex-1">
                    Price
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      className="studio-field text-sm font-normal"
                      value={form.price}
                      onChange={(e) =>
                        setForm({ ...form, price: e.target.value === '' ? 0 : Number(e.target.value) })
                      }
                    />
                  </label>
                  <label className="pkg-form-label pkg-currency-label">
                    Currency
                    <select
                      className="studio-field text-sm font-normal"
                      value={form.currencyCode}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          currencyCode: e.target.value as PackageCurrency,
                        })
                      }
                    >
                      {PACKAGE_CURRENCIES.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <p className="text-xs text-slate-500 -mt-1">
                  Preview: {formatMoney(majorToCents(form.price), form.currencyCode)}
                  {priceSuffix}
                </p>
                <label className="pkg-form-label">
                  Lessons per period
                  <input
                    type="number"
                    min={1}
                    className="studio-field text-sm font-normal"
                    value={form.lessonsPerPeriod ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        lessonsPerPeriod: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </label>
                <label className="pkg-form-label">
                  Duration (minutes)
                  <input
                    type="number"
                    min={15}
                    step={15}
                    className="studio-field text-sm font-normal"
                    value={form.lessonDurationMinutes}
                    onChange={(e) =>
                      setForm({ ...form, lessonDurationMinutes: Number(e.target.value) })
                    }
                  />
                </label>
                <label className="pkg-form-label">
                  Billing type
                  <select
                    className="studio-field text-sm font-normal"
                    value={form.billingType}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        billingType: e.target.value as 'one_time' | 'subscription',
                      })
                    }
                  >
                    <option value="subscription">Subscription</option>
                    <option value="one_time">One-time</option>
                  </select>
                </label>
                {form.billingType === 'subscription' && form.isActive && (
                  <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2">
                    Saving an active subscription package registers it with platform billing automatically.
                  </p>
                )}
                <label className="pkg-form-check">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Active (visible to students)
                </label>
                {editing && (
                  <label className="pkg-form-check">
                    <input
                      type="checkbox"
                      checked={form.applyPriceChangeToExisting}
                      onChange={(e) =>
                        setForm({ ...form, applyPriceChangeToExisting: e.target.checked })
                      }
                    />
                    Apply new price to all existing subscriptions
                  </label>
                )}
                <button type="button" className="pkg-subscribe" disabled={saving} onClick={() => void save()}>
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Create package'}
                </button>
              </div>
            </div>
          </div>

          {preview && (
            <div className="pkg-pane-b mt-4">
              <p className="text-xs text-slate-500 mb-2">Student preview</p>
              <div className="pkg-subcard">
                <div className="ps-name">{preview.name}</div>
                <div className="ps-price">
                  {formatMoney(preview.priceCents, preview.currencyCode)}
                  {preview.billingType === 'subscription' ? ' / month' : ''}
                </div>
                <p className="ps-note">{preview.description || 'Your lesson package'}</p>
              </div>
            </div>
          )}
        </PromoCardBody>
      </PromoCard>
    </div>
  );
};

export default TeacherPackagesPage;
